import { disposicionDePaginas, PX_POR_PT, type TamanoDePagina } from "@gestiondoc/shared";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

/**
 * Fase 3b · la hoja de un formato que es un archivo (cargado por un administrador desde el papel): las
 * páginas de su PDF una debajo de otra, a tamaño real, y encima la capa de tinta. Es la pareja
 * de `VistaHoja` para los formatos redactados: el mismo marco escalado a la columna, y la capa
 * recibe el mismo sistema de coordenadas (píxeles de la pila de páginas a tamaño real, el de
 * `disposicionDePaginas`, que el worker usa para devolver cada trazo a su página).
 *
 * pdf.js se carga solo cuando hace falta: es pesado y la mayoría de las pantallas no lo usan.
 */

type Estado =
  | { tipo: "cargando" }
  | { tipo: "error"; mensaje: string }
  | { tipo: "listo"; pdf: PDFDocumentProxy; tamanos: TamanoDePagina[] };

async function cargarPdfjs() {
  const [pdfjs, trabajador] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  // El `?t=js` cambia la URL del worker a propósito: hasta el 2026-10-20 nginx servía los `.mjs`
  // como octet-stream y los navegadores que abrieron un PDF en el staging guardaron esa copia
  // mala por un año (los assets de Vite llevan caché larga). Con otra URL la piden de nuevo y
  // nadie tiene que limpiar caché. Vite ya resolvió la ruta con hash; el sufijo no la rompe.
  pdfjs.GlobalWorkerOptions.workerSrc = `${trabajador.default}?t=js`;
  return pdfjs;
}

export function VistaPdf({ url, encima }: { url: string; encima?: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });

  useEffect(() => {
    let vivo = true;
    let tarea: { destroy(): Promise<void> } | null = null;
    (async () => {
      try {
        const pdfjs = await cargarPdfjs();
        const carga = pdfjs.getDocument({ url, withCredentials: true });
        tarea = carga;
        const documento = await carga.promise;
        const tamanos: TamanoDePagina[] = [];
        for (let n = 1; n <= documento.numPages; n++) {
          // El viewport ya trae el giro de la página: el tamaño es el de cómo se ve.
          const v = (await documento.getPage(n)).getViewport({ scale: 1 });
          tamanos.push({ ancho: v.width, alto: v.height });
        }
        if (vivo) setEstado({ tipo: "listo", pdf: documento, tamanos });
      } catch (e) {
        if (vivo) {
          setEstado({
            tipo: "error",
            mensaje: e instanceof Error ? e.message : "No se pudo abrir el PDF del formato",
          });
        }
      }
    })();
    return () => {
      vivo = false;
      void tarea?.destroy();
    };
  }, [url]);

  if (estado.tipo === "cargando") {
    return <div className="py-16 text-center text-[13px] text-texto-3">Abriendo el formato…</div>;
  }
  if (estado.tipo === "error") {
    return (
      <div className="py-16 text-center text-[13px] text-rojo">
        No se pudo abrir el PDF del formato: {estado.mensaje}
      </div>
    );
  }
  return <Paginas pdf={estado.pdf} tamanos={estado.tamanos} encima={encima} />;
}

function Paginas({
  pdf,
  tamanos,
  encima,
}: {
  pdf: PDFDocumentProxy;
  tamanos: TamanoDePagina[];
  encima?: ReactNode;
}) {
  const marco = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);
  const pila = useMemo(() => disposicionDePaginas(tamanos), [tamanos]);

  useEffect(() => {
    const m = marco.current;
    if (!m) return;
    const medir = () => setEscala(Math.min(1, m.clientWidth / pila.ancho));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(m);
    return () => ro.disconnect();
  }, [pila.ancho]);

  return (
    <div ref={marco} className="vista-hoja" style={{ height: pila.alto * escala }}>
      <div
        className="vista-hoja-papel"
        style={{
          width: pila.ancho,
          height: pila.alto,
          transform: `scale(${escala})`,
          transformOrigin: "top left",
        }}
      >
        {pila.paginas.map((p, i) => (
          <Pagina
            // biome-ignore lint/suspicious/noArrayIndexKey: las páginas de un PDF no cambian de orden.
            key={i}
            pdf={pdf}
            numero={i + 1}
            arriba={p.arriba}
            ancho={p.ancho}
            alto={p.alto}
          />
        ))}
        {encima}
      </div>
    </div>
  );
}

/**
 * Una página en su canvas. Se dibuja con más resolución que la de pantalla para que el zoom con
 * los dedos en la tablet no la vuelva borrosa enseguida.
 */
function Pagina({
  pdf,
  numero,
  arriba,
  ancho,
  alto,
}: {
  pdf: PDFDocumentProxy;
  numero: number;
  arriba: number;
  ancho: number;
  alto: number;
}) {
  const lienzo = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelar: (() => void) | null = null;
    let vivo = true;
    (async () => {
      const canvas = lienzo.current;
      if (!canvas) return;
      const pagina = await pdf.getPage(numero);
      if (!vivo) return;
      const nitidez = Math.min(3, (window.devicePixelRatio || 1) * 1.5);
      const viewport = pagina.getViewport({ scale: PX_POR_PT * nitidez });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const tarea = pagina.render({ canvas, viewport });
      cancelar = () => tarea.cancel();
      await tarea.promise.catch(() => {});
    })();
    return () => {
      vivo = false;
      cancelar?.();
    };
  }, [pdf, numero]);

  return (
    <canvas
      ref={lienzo}
      className="absolute left-0 bg-white shadow-[0_1px_4px_rgba(0,0,0,.18)]"
      style={{ top: arriba, width: ancho, height: alto }}
    />
  );
}
